import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import {
  Table,
  Button,
  Tag,
  Modal,
  Select,
  Input,
  Card,
  Tooltip,
} from "antd";
import {
  UserAddOutlined,
  SearchOutlined,
  SyncOutlined,
  TeamOutlined,
  PhoneOutlined,
  MailOutlined,
} from "@ant-design/icons";
import Breadcrumb from "../../components/Breadcrumb";
import { useToast } from "../../components/Toast/toastHook";
import {
  EmptyState,
  ErrorBanner,
  ErrorState,
  skeletonTableProps,
} from "../../components/states";
import {
  GET_UNASSIGNED_BUYERS,
  GET_BUYER_POOL_COUNT,
  ASSIGN_BUYER_TO_AGENT,
} from "../../graphql/fubSettingQueries";
import { GET_COMPANY_USERS } from "../../graphql/companyQueries";

const { Search } = Input;

const BuyerPool = ({ embedded = false }) => {
  const { companyId } = useParams();
  const showToast = useToast();

  const [searchText, setSearchText] = useState("");
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);

  // Queries
  const { data, loading, error, refetch } = useQuery(GET_UNASSIGNED_BUYERS, {
    variables: { companyId },
    fetchPolicy: "network-only",
  });

  const { data: countData, refetch: refetchCount } = useQuery(GET_BUYER_POOL_COUNT, {
    variables: { companyId },
    fetchPolicy: "network-only",
  });

  const { data: usersData } = useQuery(GET_COMPANY_USERS, {
    variables: { companyId },
  });

  // Mutations
  const [assignBuyer, { loading: assigning }] = useMutation(ASSIGN_BUYER_TO_AGENT, {
    onCompleted: (data) => {
      const buyer = data.assignBuyerToAgent;
      const agent = buyer.user;
      showToast(
        "success",
        `${buyer.firstName} ${buyer.lastName} assigned to ${agent.firstName} ${agent.lastName}`
      );
      setAssignModalVisible(false);
      setSelectedBuyer(null);
      setSelectedAgent(null);
      refetch();
      refetchCount();
    },
    onError: (error) => {
      showToast("error", error.message);
    },
  });

  const buyers = data?.getUnassignedBuyers || [];
  const poolCount = countData?.getBuyerPoolCount || 0;
  const agents = (usersData?.getCompanyUsers || []).filter(
    (user) => user.role?.name === "AGENT" || user.role?.name === "ADMIN 1"
  );

  // Filter buyers based on search
  const filteredBuyers = buyers.filter((buyer) => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      buyer.firstName?.toLowerCase().includes(search) ||
      buyer.lastName?.toLowerCase().includes(search) ||
      buyer.email?.toLowerCase().includes(search) ||
      buyer.phone?.includes(search) ||
      buyer.leadSource?.toLowerCase().includes(search)
    );
  });

  const handleAssignClick = (buyer) => {
    setSelectedBuyer(buyer);
    setAssignModalVisible(true);
  };

  const handleAssign = () => {
    if (!selectedBuyer || !selectedAgent) {
      showToast("error", "Please select an agent");
      return;
    }
    assignBuyer({
      variables: {
        buyerId: selectedBuyer._id,
        userId: selectedAgent,
      },
    });
  };

  const columns = [
    {
      title: "Name",
      key: "name",
      render: (_, record) => (
        <div>
          <span className="font-medium">
            {record.firstName} {record.lastName}
          </span>
          {record.fubId && (
            <Tag color="blue" className="ml-2" size="small">
              FUB
            </Tag>
          )}
        </div>
      ),
      sorter: (a, b) => a.firstName.localeCompare(b.firstName),
    },
    {
      title: "Contact",
      key: "contact",
      render: (_, record) => (
        <div className="space-y-1">
          {record.email && (
            <div className="flex items-center gap-1 text-sm text-gray-600">
              <MailOutlined />
              <a href={`mailto:${record.email}`}>{record.email}</a>
            </div>
          )}
          {record.phone && (
            <div className="flex items-center gap-1 text-sm text-gray-600">
              <PhoneOutlined />
              <a href={`tel:${record.phone}`}>{record.phone}</a>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Source",
      key: "source",
      dataIndex: "leadSource",
      render: (source) => source || <span className="text-gray-400">-</span>,
    },
    {
      title: "FUB Stage",
      key: "fubStage",
      dataIndex: "fubStage",
      render: (stage) =>
        stage ? (
          <Tag color="cyan">{stage}</Tag>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      title: "Tags",
      key: "tags",
      dataIndex: "tags",
      render: (tags) =>
        tags?.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {tags.slice(0, 3).map((tag, idx) => (
              <Tag key={idx} size="small">
                {tag}
              </Tag>
            ))}
            {tags.length > 3 && (
              <Tooltip title={tags.slice(3).join(", ")}>
                <Tag size="small">+{tags.length - 3}</Tag>
              </Tooltip>
            )}
          </div>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      title: "Added",
      key: "createdAt",
      dataIndex: "createdAt",
      render: (date) => (date ? new Date(date).toLocaleDateString() : "-"),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      defaultSortOrder: "descend",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<UserAddOutlined />}
          onClick={() => handleAssignClick(record)}
          style={{ backgroundColor: "#b4c540", borderColor: "#b4c540" }}
        >
          Assign
        </Button>
      ),
    },
  ];

  const firstLoad = loading && !filteredBuyers.length;

  return (
    <div className={embedded ? "" : "m-4"}>
      {!embedded && (
        <Breadcrumb
          title="Buyer Pool"
          items={[
            { title: "Dashboard", href: `/company/${companyId}/dashboard` },
            { title: "Buyer Pool" },
          ]}
        />
      )}

      {/* Stats Card */}
      <Card className={embedded ? "mb-4" : "mt-6 mb-6"}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-blue-100 p-4 rounded-full">
              <TeamOutlined className="text-2xl text-blue-600" />
            </div>
            <div>
              <h3 className="text-2xl font-bold">{poolCount}</h3>
              <p className="text-gray-500">Unassigned Buyers</p>
            </div>
          </div>
          <Button
            icon={<SyncOutlined />}
            onClick={() => {
              refetch();
              refetchCount();
            }}
          >
            Refresh
          </Button>
        </div>
      </Card>

      {/* Search and Table */}
      <Card>
        <div className="mb-4">
          <Search
            placeholder="Search by name, email, phone, or source..."
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            onChange={(e) => setSearchText(e.target.value)}
            style={{ maxWidth: 400 }}
          />
        </div>

        {error && !filteredBuyers.length ? (
          <ErrorState onRetry={() => refetch()} />
        ) : (
          <>
            {error && <ErrorBanner onRetry={() => refetch()} />}
            <Table
              rowKey="_id"
              {...(firstLoad
                ? skeletonTableProps(columns)
                : {
                    columns,
                    dataSource: filteredBuyers,
                    pagination: {
                      pageSize: 10,
                      showSizeChanger: true,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} buyers`,
                    },
                  })}
              locale={{
                emptyText: searchText ? (
                  <EmptyState
                    title="No buyers match your search"
                    description={`Nothing found for "${searchText}".`}
                    actionLabel="Clear search"
                    onAction={() => setSearchText("")}
                  />
                ) : (
                  <EmptyState
                    title="No unassigned buyers in the pool"
                    description="Buyers without an assigned agent will appear here."
                  />
                ),
              }}
            />
          </>
        )}
      </Card>

      {/* Assign Modal */}
      <Modal
        title={
          selectedBuyer
            ? `Assign ${selectedBuyer.firstName} ${selectedBuyer.lastName}`
            : "Assign Buyer"
        }
        open={assignModalVisible}
        onCancel={() => {
          setAssignModalVisible(false);
          setSelectedBuyer(null);
          setSelectedAgent(null);
        }}
        onOk={handleAssign}
        okText="Assign"
        confirmLoading={assigning}
        okButtonProps={{
          style: { backgroundColor: "#b4c540", borderColor: "#b4c540" },
        }}
      >
        {selectedBuyer && (
          <div className="mb-4 p-3 bg-gray-50 rounded">
            <p>
              <strong>Email:</strong> {selectedBuyer.email || "N/A"}
            </p>
            <p>
              <strong>Phone:</strong> {selectedBuyer.phone || "N/A"}
            </p>
            <p>
              <strong>Source:</strong> {selectedBuyer.leadSource || "N/A"}
            </p>
          </div>
        )}

        <p className="mb-2">Select an agent to assign this buyer to:</p>
        <Select
          placeholder="Select an agent"
          style={{ width: "100%" }}
          size="large"
          value={selectedAgent}
          onChange={setSelectedAgent}
          showSearch
          filterOption={(input, option) =>
            option.label.toLowerCase().includes(input.toLowerCase())
          }
          options={agents.map((agent) => ({
            value: agent.id || agent._id,
            label: `${agent.firstName} ${agent.lastName} (${agent.email})`,
          }))}
        />
      </Modal>
    </div>
  );
};

export default BuyerPool;
